package features.payment

import features.booking.SmartVanColors
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.CreditCard
import androidx.compose.material.icons.filled.Receipt
import androidx.compose.material.icons.filled.Store
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.LaunchedEffect
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import features.booking.BookingViewModel
import features.booking.SeatStatus

@Composable
fun PaymentScreen(
    bookingViewModel: BookingViewModel,
    paymentViewModel: PaymentViewModel,
    onBackClick: () -> Unit,
    onSpeiGenerated: () -> Unit,
    onOxxoGenerated: () -> Unit = {}
) {
    val seats by bookingViewModel.seats.collectAsState()
    val isProcessing by paymentViewModel.isProcessing.collectAsState()
    val errorPago by paymentViewModel.errorPago.collectAsState()
    val precios by bookingViewModel.precios.collectAsState()
    val origen by bookingViewModel.origen.collectAsState()
    val destino by bookingViewModel.destino.collectAsState()
    val fecha by bookingViewModel.fecha.collectAsState()
    val horario by bookingViewModel.horario.collectAsState()

    val boletoPendiente by bookingViewModel.boletoPendiente.collectAsState()
    val selectedSeats = seats.filter { it.status == SeatStatus.SELECTED }
    val boletoBase = boletoPendiente?.totalPagado ?: (selectedSeats.size * (precios["base"] ?: 350))
    val seatsText = (boletoPendiente?.asientos ?: selectedSeats.map { it.id }).joinToString(", ")
    val rutaCompleta = "$origen - $destino"
    val requiereFactura = boletoPendiente?.requiereFactura ?: false

    var metodoPago by remember { mutableStateOf("spei") }
    var errorEmail by remember { mutableStateOf(false) }

    // Abrir el Checkout de Stripe en el navegador cuando el VM entregue la URL.
    val uriHandler = LocalUriHandler.current
    val checkoutTarjetaUrl by paymentViewModel.checkoutTarjetaUrl.collectAsState()
    var esperandoTarjeta by remember { mutableStateOf(false) }
    var sessionIdTarjeta by remember { mutableStateOf("") }
    LaunchedEffect(checkoutTarjetaUrl) {
        val u = checkoutTarjetaUrl
        if (!u.isNullOrBlank()) {
            // El contador local ya no aplica: de aquí en adelante los asientos los sostiene
            // el pago_pendiente del servidor. Si siguiera corriendo, al vencer marcaría
            // "sesión expirada" y liberaría asientos ya pagados.
            bookingViewModel.detenerCuentaRegresivaPorPago()
            org.example.abigail.abrirVentanaPago(u)
            sessionIdTarjeta = paymentViewModel.sessionIdTarjeta()
            paymentViewModel.consumirCheckoutUrl()
            esperandoTarjeta = true   // pasamos a la pantalla de "esperando confirmación"
        }
    }

    // Cuando el listener detecta el pago de tarjeta confirmado (vía espejo pagos_confirmados/{cs_id}),
    // salimos del estado de espera para mostrar el éxito. Antes la app se quedaba en selección de método.
    val pagoConfirmadoTarjeta by paymentViewModel.pagoConfirmado.collectAsState()

    // Costo por servicio: Card y OXXO siempre pasan por Stripe → siempre cobran recargo.
    // SPEI solo cobra recargo cuando stripeActivo=true (customer_balance). Si SPEI cae a
    // manual (CLABE del negocio), sin recargo.
    // El monto sale de catalogos/precios/costoServicio (lo edita el admin en su panel).
    val stripeActivo by network.ConfigPagos.stripeActivo.collectAsState()
    val costoServicio = when (metodoPago) {
        "card", "oxxo" -> features.booking.CalculoPrecios.costoServicioPorMetodo(metodoPago, precios)
        "spei" -> if (stripeActivo) features.booking.CalculoPrecios.costoServicioPorMetodo("spei", precios) else 0
        else -> 0
    }
    val totalToPay = boletoBase + costoServicio

    // Pantalla de ESPERA / ÉXITO de tarjeta: al abrir el Checkout mostramos este estado en vez de
    // dejar al usuario en la selección de método. Reacciona al pago confirmado (listener).
    if (esperandoTarjeta) {
        Column(
            modifier = Modifier.fillMaxSize().padding(28.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            if (pagoConfirmadoTarjeta) {
                // El pago se confirmó acá (listener). La pestaña del Checkout ya no sirve:
                // se cierra para dejar al usuario en la pestaña donde llenó el formulario.
                LaunchedEffect(Unit) { org.example.abigail.cerrarVentanaPago() }

                // La compra de clientes es SOLO web: ahí el flujo lo toma App.kt y muestra los
                // boletos en pantalla. La APK es para administración y choferes, así que aquí
                // no se ofrece descarga de boletos; el correo es la entrega.
                Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(72.dp))
                Spacer(modifier = Modifier.height(16.dp))
                Text("¡Compra exitosa!", style = MaterialTheme.typography.headlineSmall, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, textAlign = TextAlign.Center)
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    "Tus boletos con código QR se enviaron a tu correo electrónico.",
                    color = Color.Gray, textAlign = TextAlign.Center
                )
                Spacer(modifier = Modifier.height(24.dp))
                Button(
                    onClick = {
                        esperandoTarjeta = false
                        paymentViewModel.resetearEstado()
                        // Dejar el formulario en blanco: en la APK no hay recarga que lo haga.
                        bookingViewModel.limpiarTrasCompraExitosa()
                        // En web recarga (y de paso limpia); en nativo es no-op.
                        org.example.abigail.recargarPagina()
                        onBackClick()
                    },
                    modifier = Modifier.fillMaxWidth().height(52.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro),
                    shape = RoundedCornerShape(14.dp)
                ) { Text("Finalizar", fontWeight = FontWeight.Bold, color = Color.White) }
            } else {
                CircularProgressIndicator(color = SmartVanColors.AzulOscuro, modifier = Modifier.size(56.dp))
                Spacer(modifier = Modifier.height(20.dp))
                Text("Esperando confirmación del pago…", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, textAlign = TextAlign.Center)
                Spacer(modifier = Modifier.height(8.dp))
                Text("Completa el pago en la pestaña de Stripe que se abrió. Esta pantalla se actualizará sola cuando el pago se confirme.", color = Color.Gray, textAlign = TextAlign.Center)
                Spacer(modifier = Modifier.height(24.dp))
                OutlinedButton(
                    onClick = { esperandoTarjeta = false; paymentViewModel.resetearEstado() },
                    modifier = Modifier.fillMaxWidth().height(48.dp),
                    shape = RoundedCornerShape(14.dp)
                ) { Text("Cancelar y elegir otro método", color = Color.Gray) }
            }
        }
        return
    }

    Column(
        modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            "Método de Pago",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Black,
            color = SmartVanColors.AzulOscuro
        )
        Text("Elige cómo deseas pagar", color = Color.Gray, modifier = Modifier.padding(top = 4.dp, bottom = 20.dp))

        // Resumen compacto
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro),
            shape = RoundedCornerShape(14.dp)
        ) {
            Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
                Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(rutaCompleta, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                        Text("$fecha • $horario", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                        Text("Asientos: $seatsText", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    }
                    Text("$$totalToPay MXN", fontWeight = FontWeight.Black, color = SmartVanColors.Verde, style = MaterialTheme.typography.titleLarge)
                }
                if (costoServicio > 0) {
                    Spacer(modifier = Modifier.height(6.dp))
                    HorizontalDivider(color = Color.LightGray.copy(alpha = 0.5f))
                    Spacer(modifier = Modifier.height(4.dp))
                    Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                        Text("Boleto", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                        Text("$$boletoBase MXN", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                    }
                    Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                        Text("Costo por servicio", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                        Text("$$costoServicio MXN", style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                    }
                }
            }
        }

        // Indicador de factura solicitada
        if (requiereFactura) {
            Spacer(modifier = Modifier.height(8.dp))
            Card(
                colors = CardDefaults.cardColors(containerColor = Color(0xFFF3E5F5)),
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Receipt, contentDescription = null, tint = Color(0xFF7B1FA2), modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text("Factura solicitada", fontWeight = FontWeight.Bold, color = Color(0xFF7B1FA2), style = MaterialTheme.typography.bodySmall)
                        Text("IVA (16%) incluido en el total", style = MaterialTheme.typography.labelSmall, color = Color(0xFF9C27B0))
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(24.dp))

        // Selector SPEI
        Card(
            modifier = Modifier.fillMaxWidth().clickable { metodoPago = "spei" },
            colors = CardDefaults.cardColors(
                containerColor = if (metodoPago == "spei") SmartVanColors.FondoCyan else Color.White
            ),
            border = BorderStroke(
                2.dp,
                if (metodoPago == "spei") SmartVanColors.CyanAccent else Color.LightGray
            ),
            shape = RoundedCornerShape(14.dp)
        ) {
            Row(modifier = Modifier.fillMaxWidth().padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier.size(44.dp).background(
                        if (metodoPago == "spei") SmartVanColors.CyanAccent.copy(alpha = 0.15f) else SmartVanColors.FondoGrisClaro,
                        CircleShape
                    ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Icons.Filled.AccountBalance, contentDescription = null, tint = if (metodoPago == "spei") SmartVanColors.CyanAccent else Color.Gray, modifier = Modifier.size(22.dp))
                }
                Spacer(modifier = Modifier.width(14.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text("Transferencia SPEI", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Text("Pago inmediato desde tu banca en línea", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                }
                RadioButton(
                    selected = metodoPago == "spei",
                    onClick = { metodoPago = "spei" },
                    colors = RadioButtonDefaults.colors(selectedColor = SmartVanColors.CyanAccent)
                )
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        // Selector OXXO
        Card(
            modifier = Modifier.fillMaxWidth().clickable { metodoPago = "oxxo" },
            colors = CardDefaults.cardColors(
                containerColor = if (metodoPago == "oxxo") SmartVanColors.FondoNaranja else Color.White
            ),
            border = BorderStroke(
                2.dp,
                if (metodoPago == "oxxo") SmartVanColors.Naranja else Color.LightGray
            ),
            shape = RoundedCornerShape(14.dp)
        ) {
            Row(modifier = Modifier.fillMaxWidth().padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier.size(44.dp).background(
                        if (metodoPago == "oxxo") SmartVanColors.Naranja.copy(alpha = 0.15f) else SmartVanColors.FondoGrisClaro,
                        CircleShape
                    ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Icons.Filled.Store, contentDescription = null, tint = if (metodoPago == "oxxo") SmartVanColors.Naranja else Color.Gray, modifier = Modifier.size(22.dp))
                }
                Spacer(modifier = Modifier.width(14.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text("Pago en OXXO", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Text("Paga en efectivo con referencia (24 horas)", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                }
                RadioButton(
                    selected = metodoPago == "oxxo",
                    onClick = { metodoPago = "oxxo" },
                    colors = RadioButtonDefaults.colors(selectedColor = SmartVanColors.Naranja)
                )
            }
        }

        if (metodoPago == "oxxo") {
            Spacer(modifier = Modifier.height(8.dp))
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    "Nota: Los asientos no se bloquean hasta confirmar el pago. Si alguien más paga primero, se te notificará para reembolso.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color(0xFF6D4C00),
                    modifier = Modifier.padding(12.dp)
                )
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        // Selector TARJETA
        Card(
            modifier = Modifier.fillMaxWidth().clickable { metodoPago = "card" },
            colors = CardDefaults.cardColors(
                containerColor = if (metodoPago == "card") SmartVanColors.FondoCyan else Color.White
            ),
            border = BorderStroke(
                2.dp,
                if (metodoPago == "card") SmartVanColors.AzulOscuro else Color.LightGray
            ),
            shape = RoundedCornerShape(14.dp)
        ) {
            Row(modifier = Modifier.fillMaxWidth().padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier.size(44.dp).background(
                        if (metodoPago == "card") SmartVanColors.AzulOscuro.copy(alpha = 0.15f) else SmartVanColors.FondoGrisClaro,
                        CircleShape
                    ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Icons.Filled.CreditCard, contentDescription = null, tint = if (metodoPago == "card") SmartVanColors.AzulOscuro else Color.Gray, modifier = Modifier.size(22.dp))
                }
                Spacer(modifier = Modifier.width(12.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text("Tarjeta de crédito/débito", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                    Text("Pago inmediato en Stripe", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                }
                RadioButton(
                    selected = metodoPago == "card",
                    onClick = { metodoPago = "card" },
                    colors = RadioButtonDefaults.colors(selectedColor = SmartVanColors.AzulOscuro)
                )
            }
        }

        // Aviso si Stripe está apagado: los 3 métodos caen a transferencia manual con CLABE.
        if (!stripeActivo) {
            Spacer(modifier = Modifier.height(8.dp))
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    "Cobros en línea temporalmente pausados: al continuar recibirás una CLABE para hacer la transferencia y enviar tu comprobante por WhatsApp.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color(0xFF6D4C00),
                    modifier = Modifier.padding(12.dp)
                )
            }
        }

        Spacer(modifier = Modifier.height(28.dp))

        val btnColor = when (metodoPago) {
            "spei" -> SmartVanColors.CyanAccent
            "oxxo" -> SmartVanColors.Naranja
            else -> SmartVanColors.AzulOscuro
        }
        // stripeActivo solo afecta SPEI (customer_balance). Tarjeta y OXXO siempre pasan por Stripe.
        val btnText = when (metodoPago) {
            "spei" -> if (stripeActivo) "Generar referencia SPEI" else "Ver datos para transferir"
            "oxxo" -> "Continuar con pago en OXXO"
            "card" -> "Continuar con pago en tarjeta"
            else -> "Continuar"
        }

        if (errorEmail) {
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
            ) {
                Text(
                    "Ingresa un correo electrónico válido en los datos del pasajero para recibir tus boletos.",
                    color = Color.Red,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(12.dp)
                )
            }
        }

        // FIX #14: Resetear flag cuando hay error para que el usuario pueda reintentar
        var yaSePresiono by remember { mutableStateOf(false) }
        // Si errorPago cambia a non-null, significa que falló → permitir reintentar
        LaunchedEffect(errorPago) {
            if (errorPago != null) yaSePresiono = false
        }
        Button(
            onClick = {
                if (yaSePresiono) return@Button
                val pasajerosMap = bookingViewModel.pasajeros.value
                val emailPasajero = pasajerosMap.values.firstOrNull()?.correo ?: ""
                if (emailPasajero.isBlank() || !emailPasajero.contains("@") || !emailPasajero.contains(".")) {
                    errorEmail = true
                    return@Button
                }
                errorEmail = false
                yaSePresiono = true
                // IDA: usar SIEMPRE los pasajeros del boleto pendiente (los _pasajeros actuales
                // podrían ser los de la vuelta si es ida y vuelta).
                val pasajerosIdaBoleto = boletoPendiente?.pasajeros ?: pasajerosMap.values.toList()
                val pasajerosJson = features.booking.PasajeroInfo.listaAJsonString(pasajerosIdaBoleto)
                val asientosArray = (boletoPendiente?.asientos ?: selectedSeats.map { it.id })
                val correoTitular = pasajerosIdaBoleto.firstOrNull()?.correo ?: ""

                val puntoSubidaVal = boletoPendiente?.puntoSubida ?: ""
                val urlSalidaVal = boletoPendiente?.urlSalida ?: ""
                val esIdaYVueltaVal = boletoPendiente?.esIdaYVuelta ?: false
                val fechaVueltaVal = boletoPendiente?.fechaVuelta ?: ""
                val horarioVueltaVal = boletoPendiente?.horarioVuelta ?: ""
                val rutaVueltaVal = boletoPendiente?.rutaVuelta ?: ""
                val asientosVueltaVal = boletoPendiente?.asientosVuelta ?: emptyList()
                val pasajerosVueltaJson = features.booking.PasajeroInfo.listaAJsonString(boletoPendiente?.pasajerosVuelta ?: emptyList())
                val puntoSubidaVueltaVal = boletoPendiente?.puntoSubidaVuelta ?: ""
                val requiereFacturaVal = boletoPendiente?.requiereFactura ?: false
                val rfcVal = boletoPendiente?.rfc ?: ""
                val razonSocialVal = boletoPendiente?.razonSocial ?: ""
                val regimenFiscalVal = boletoPendiente?.regimenFiscal ?: ""
                val usoCfdiVal = boletoPendiente?.usoCfdi ?: ""
                val cpFiscalVal = boletoPendiente?.cpFiscal ?: ""

                when (metodoPago) {
                    "spei" -> paymentViewModel.generateSpeiReference(
                        monto = totalToPay, ruta = rutaCompleta, fecha = fecha, horario = horario,
                        asientos = seatsText, email = correoTitular,
                        pasajeros = pasajerosJson, asientosArray = asientosArray,
                        origenDestino = rutaCompleta, totalPagado = totalToPay,
                        puntoSubida = puntoSubidaVal, urlSalida = urlSalidaVal,
                        esIdaYVuelta = esIdaYVueltaVal, fechaVuelta = fechaVueltaVal,
                        horarioVuelta = horarioVueltaVal, rutaVuelta = rutaVueltaVal,
                        asientosVueltaArray = asientosVueltaVal, pasajerosVuelta = pasajerosVueltaJson,
                        puntoSubidaVuelta = puntoSubidaVueltaVal,
                        requiereFactura = requiereFacturaVal, rfc = rfcVal,
                        razonSocial = razonSocialVal, regimenFiscal = regimenFiscalVal,
                        usoCfdi = usoCfdiVal, cpFiscal = cpFiscalVal
                    ) { bookingViewModel.detenerCuentaRegresivaPorPago(); onSpeiGenerated() }
                    "oxxo" -> paymentViewModel.generateOxxoReference(
                        monto = totalToPay, ruta = rutaCompleta, fecha = fecha, horario = horario,
                        asientos = seatsText, email = correoTitular,
                        pasajeros = pasajerosJson, asientosArray = asientosArray,
                        origenDestino = rutaCompleta, totalPagado = totalToPay,
                        puntoSubida = puntoSubidaVal, urlSalida = urlSalidaVal,
                        esIdaYVuelta = esIdaYVueltaVal, fechaVuelta = fechaVueltaVal,
                        horarioVuelta = horarioVueltaVal, rutaVuelta = rutaVueltaVal,
                        asientosVueltaArray = asientosVueltaVal, pasajerosVuelta = pasajerosVueltaJson,
                        puntoSubidaVuelta = puntoSubidaVueltaVal,
                        requiereFactura = requiereFacturaVal, rfc = rfcVal,
                        razonSocial = razonSocialVal, regimenFiscal = regimenFiscalVal,
                        usoCfdi = usoCfdiVal, cpFiscal = cpFiscalVal
                    ) { bookingViewModel.detenerCuentaRegresivaPorPago(); onOxxoGenerated() }
                    "card" -> paymentViewModel.generateCardCheckout(
                        monto = totalToPay, ruta = rutaCompleta, fecha = fecha, horario = horario,
                        asientos = seatsText, email = correoTitular,
                        pasajeros = pasajerosJson, asientosArray = asientosArray,
                        origenDestino = rutaCompleta, totalPagado = totalToPay,
                        puntoSubida = puntoSubidaVal, urlSalida = urlSalidaVal,
                        esIdaYVuelta = esIdaYVueltaVal, fechaVuelta = fechaVueltaVal,
                        horarioVuelta = horarioVueltaVal, rutaVuelta = rutaVueltaVal,
                        asientosVueltaArray = asientosVueltaVal, pasajerosVuelta = pasajerosVueltaJson,
                        puntoSubidaVuelta = puntoSubidaVueltaVal,
                        requiereFactura = requiereFacturaVal, rfc = rfcVal,
                        razonSocial = razonSocialVal, regimenFiscal = regimenFiscalVal,
                        usoCfdi = usoCfdiVal, cpFiscal = cpFiscalVal
                    )
                }
            },
            modifier = Modifier.fillMaxWidth().height(56.dp),
            colors = ButtonDefaults.buttonColors(containerColor = btnColor),
            shape = RoundedCornerShape(14.dp),
            enabled = !isProcessing
        ) {
            if (isProcessing) {
                CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Generando referencia...")
            } else {
                Text(btnText, fontWeight = FontWeight.Bold, color = Color.White)
            }
        }

        if (errorPago != null) {
            Spacer(modifier = Modifier.height(12.dp))
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(14.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        errorPago ?: "",
                        color = Color(0xFFD32F2F),
                        fontWeight = FontWeight.Bold,
                        style = MaterialTheme.typography.bodySmall
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    OutlinedButton(
                        onClick = { paymentViewModel.limpiarError() },
                        border = BorderStroke(1.dp, Color(0xFFD32F2F)),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text("Reintentar", color = Color(0xFFD32F2F), fontWeight = FontWeight.Bold)
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        TextButton(onClick = {
            paymentViewModel.limpiarError()
            onBackClick()
        }) {
            Text("Regresar", color = Color.Gray)
        }
    }
}
